import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnInit,
  OnDestroy,
  Output,
  NgZone,
} from '@angular/core';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { ViewChild } from '@angular/core';
import { Toast, ToastComponent } from '../toast/toast.component';
import { MatSort, SortDirection } from '@angular/material/sort';
import { FilterConfig, GridConfig } from './grid-model/grid-config.model';
import { GridDataService } from './grid-service/grid-data.service';
import { DatePipe } from '@angular/common';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, finalize, switchMap, takeUntil } from 'rxjs/operators';
import moment from 'moment';
import 'moment-timezone';
import cronstrue from 'cronstrue';
import { marked } from 'marked';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ACKNOWLEDGEABLE_ERROR_STATUS_CODES,
  MAX_INT_32,
  ACKNOWLEDGEABLE_SUCCESS_STATUS_CODES,
} from '../constants/values';
import { BREADCRUMB_NAMES } from '../constants/breadCrumbPaths';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { ColumnConfig, computeFitSettings, estimateColumnWidth, ExportPdfOptions, formatCellByType, innerWidth, timestamp } from '../../shared/export-to-pdf/pdfHelpers';
import { API_ENDPOINTS } from '../constants/apiEnpoints';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { ColumnPreferencesService } from '../column-preference/columnpreference.service';

const vfs = (pdfFonts as any).pdfMake?.vfs ?? (pdfFonts as any).vfs;
(pdfMake as any).vfs = vfs;

export interface ColumnPreference {
  PreferenceKeyName: string;
  HiddenColumns: Set<string>;
}

@Component({
  selector: 'app-dynamic-grid',
  templateUrl: './dynamic-grid.component.html',
  styleUrls: ['./dynamic-grid.component.scss'],
  providers: [DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DynamicGridComponent implements OnInit {
  private readonly dropdownRegionSelector = '[data-grid-dropdown-region]';
  @ViewChild('gridRoot', { static: true }) gridRoot!: ElementRef;
  @Input() gridConfig!: GridConfig;
  @Input() getDataUrl!: string;
  @Input() postUrl!: string;
  @Input() deleteUrl!: string;
  @Input() toggleUrl!: string;
  @Input() extraActions: { label: string; icon?: string; action: () => void; cssClass?: string }[] = [];
  @Input() customIsErrorLog: (element: any) => boolean;
  @Input() isRowClickable: boolean = false;
  @Input() initialShowDisabled: boolean = false;
  @Input() initialShowPendingInstallation: boolean = false;
  @Input() preloadedData: any[] = [];
  @Input() installerUrl: string = '';
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @Output() addClicked = new EventEmitter<boolean>();
  @Output() editClicked = new EventEmitter<any>();
  @Output() edit2Clicked = new EventEmitter<any>();
  @Output() deleteClicked = new EventEmitter<any>();
  @ViewChild(ToastComponent) toastComponent!: ToastComponent;
  @Input() onAdd!: Function;
  @Input() onEdit!: Function;
  @Output() onToggleShowToastEmitter: EventEmitter<Toast> =
    new EventEmitter<Toast>();
  @Output() onRowClick: EventEmitter<any> = new EventEmitter<any>();
  dataSource = new MatTableDataSource();
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();
  private subscriptions: Subscription[] = [];
  private activeDataFetchSubscription?: Subscription;
  private fetchRequestId = 0;
  displayedColumns!: string[];
  isDropdownOpen: boolean = false;
  hasFilters: boolean;
  isExportMenuOpen: boolean;
  isLoading: boolean = true;
  isExporting: boolean = false;
  showBlockingLoader: boolean = true;
  pageNumber: number = 1;
  pageSize: number = 50;
  searchQuery: string = '';
  @Input() sortBy: string = '';
  showErrorCard: boolean = false;
  errorTitle: string = '';
  errorMessage: string = '';
  @Input() sortOrder: string = '';
  totalItems: number;
  isServerSidePagination: boolean = true;
  pageChanged: boolean = true;
  pageSizeOptions: number[] = [50, 100, 150, 200, MAX_INT_32];
  refreshTimeout: any;
  filters: { [key: string]: any } = {};
  isFilterDropdownOpen: { [key: string]: boolean } = {};
  customDateRange = {
    from: '',
    to: '',
  };
  isSelected: any;
  userRole = localStorage.getItem('userRole');
  userEmail = localStorage.getItem('userEmail');
  filtered: { [key: string]: any } = {};
  appliedFilter: boolean = false;
  resetAll: boolean = false;
  uniqueTableName: string;

  emptyDataMessage: string = 'No data found at this moment';
  private _totalRecordsCount: number = 0;

  isColumnSelectorOpen = false;
  isSavingPreferences = false;
  visibleColumns: Set<string> = new Set();
  private userId: string | null = null;
  columnPreference: ColumnPreference;
  exportFileName: string;

  // Practice Actions Menu
  practiceMenuOpen: { [key: string]: boolean } = {};
  showResetConfirmation = false;
  selectedPractice: any = null;

  isDateInputDisabled: boolean = false;
  minFromDate: string = '';
  maxToDate: string = '';
  showDateFilterDropdown = false;

  constructor(
    private gridDataService: GridDataService,
    public dialog: MatDialog,
    public _router: Router,
    private activatedRoute: ActivatedRoute,
    private elementRef: ElementRef,
    private changeDetectorRef: ChangeDetectorRef,
    private datePipe: DatePipe,
    private columnPrefService: ColumnPreferencesService,
    private zone: NgZone,
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth
  ) { }
  showDropdown = false;
  isViewOptionsOpen = false;
  isFromDisabledPractice = false;
  isFromPendingInstallation = false;
  isFromInstalledPractice = false;
  isFromGoLivePractice = false;
  isFromActiveUsers = false;
  isFromActivePMS = false;
  selectedViewOption: string = 'all';
  lowResourceCount = 0;
  public get router(): Router {
    return this._router;
  }
  public set router(value: Router) {
    this._router = value;
  }

  @HostListener('document:pointerdown', ['$event.target'])
  onDocumentPointerDown(target: EventTarget | null) {
    if (!(target instanceof Node)) {
      return;
    }

    const gridRoot = this.gridRoot?.nativeElement as Node | undefined;
    if (!gridRoot) {
      return;
    }

    const clickedInsideGrid = gridRoot.contains(target);
    if (!clickedInsideGrid) {
      this.closeAllDropdowns();
      return;
    }

    if (!this.isWithinDropdownRegion(target)) {
      this.closeAllDropdowns();
    }
  }

  private isWithinDropdownRegion(target: Node): boolean {
    let currentNode: Node | null = target;

    while (currentNode) {
      if (
        currentNode instanceof Element &&
        currentNode.matches(this.dropdownRegionSelector)
      ) {
        return true;
      }

      currentNode = currentNode.parentNode;
    }

    return false;
  }


  closeAllDropdowns() {
    if (this.isExporting) {
      return;
    }

    this.isDropdownOpen = false;
    Object.keys(this.isFilterDropdownOpen).forEach((key) => {
      this.isFilterDropdownOpen[key] = false;
    });
    this.showDropdown = false;
    this.isViewOptionsOpen = false;
    this.isColumnSelectorOpen = false;
    Object.keys(this.practiceMenuOpen).forEach((key) => {
      this.practiceMenuOpen[key] = false;
    });
  }

  getGridColumnNames() {
    return this.getBaseColumns()
      .filter(col => this.visibleColumns.has(col));
  }

  getBaseColumns(): string[] {
    const cols = this.gridConfig.columns.map(c => c.jsonKey);

    if (this.gridConfig.editable || this.gridConfig.deletable) {
      return ['action', ...cols];
    }

    return cols;
  }

  async ngOnInit(): Promise<void> {
    this.hasFilters = this.gridConfig.filters == undefined ? false : true;
    this.uniqueTableName = this.gridConfig.uniqueTableName;
    this.applyDefaultFilters();
    this.checkIfFromDisabledPractice();
    this.checkIfFromPendingInstallation();
    this.checkIfFromInstalledPractice();
    this.checkIfFromGoLivePractice();
    this.checkIfFromActiveUsers();
    this.checkIfFromActivePMS();
    if (this.initialShowDisabled) {
      this.filters['isActive'] = false;
      setTimeout(() => {
        this.isViewOptionsOpen = true;
      }, 500);
    }
    if (this.initialShowPendingInstallation) {
      this.filters['isInstalled'] = false;
      setTimeout(() => {
        this.isViewOptionsOpen = true;
      }, 500);
    }

    setTimeout(() => {
      this.columnPreference = this.getPreferenceKeyName();
      this.loadColumnPreferences(this.columnPreference.PreferenceKeyName);
    }, 0);

    // Delay fetchData to ensure route parameter checks complete first
    setTimeout(() => {
      this.fetchData(true);
    }, 0);

    this.setupColumns();
    const currentDateTime = new Date();
    this.customDateRange.from = this.formatDateTime(currentDateTime);
    this.customDateRange.to = this.formatDateTime(currentDateTime);

    this.searchSubject.pipe(
      debounceTime(500),
      takeUntil(this.destroy$)
    ).subscribe((searchQuery) => {
      this.searchQuery = searchQuery;
      this.pageNumber = 1;
      this.fetchData(false);
    });

    this.configureMarked();
  }

  private applyDefaultFilters(): void {
    if (!this.gridConfig?.defaultFilters) {
      return;
    }

    Object.entries(this.gridConfig.defaultFilters).forEach(([key, value]) => {
      if (this.filters[key] === undefined) {
        this.filters[key] = value;
      }
    });
  }

  formatNumber(value: number | null): string | number {
    return value ? new Intl.NumberFormat('en-US').format(value) : value;
  }

  async fetchData(showBlockingLoader: boolean = false): Promise<void> {
    this.initializeDefaultFilterSelections();
    this.activeDataFetchSubscription?.unsubscribe();
    const currentFetchRequestId = ++this.fetchRequestId;
    this.isLoading = true;
    this.showBlockingLoader = showBlockingLoader;
    try {
      // If preloaded data is provided, use it instead of fetching
      if (this.preloadedData && this.preloadedData.length > 0) {
        this.totalItems = this.preloadedData.length;

        this.zone.run(() => {
          this.dataSource.data = [...this.preloadedData];
          this.isLoading = false;
          this.changeDetectorRef.detectChanges();
        });
        return;
      }

      // Skip API call if no URL provided
      if (!this.getDataUrl) {
        this.zone.run(() => {
          this.isLoading = false;
          this.changeDetectorRef.detectChanges();
        });
        return;
      }

      // Fetch data from the service
      const subscription = this.gridDataService
        .fetchData(
          this.getDataUrl,
          this.pageNumber,
          this.pageSize,
          this.searchQuery,
          this.sortBy,
          this.sortOrder,
          this.filters
        )
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => {
            if (currentFetchRequestId !== this.fetchRequestId) {
              return;
            }
            this.zone.run(() => {
              this.isLoading = false;
              this.showBlockingLoader = false;
              this.changeDetectorRef.detectChanges();
            });
          })
        )
        .subscribe((response) => {
          if (currentFetchRequestId !== this.fetchRequestId) {
            return;
          }

          this.zone.run(() => {

            if (response.isSuccess === false) {
              this.handleError({
                status: response.statusCode,
                error: {
                  errorMessage: response.errorMessage,
                  detailedErrorMessage: response.detailedErrorMessage,
                },
              });
              return;
            }

            if (this.uniqueTableName === 'UserRoleManagement') {
              const originalCount = response.data.length;

              response.data = response.data.filter(
                (item) =>
                  item.email != this.userEmail &&
                  item.email != 'pms_admin@dentalcorp.ca'
              );

              const filteredCount = response.data.length;
              const removedCount = originalCount - filteredCount;

              response.totalRecordsCount = response.totalRecordsCount
                ? response.totalRecordsCount - removedCount
                : response.totalRecordsCount;
            }

            // Transform practice log messages
            if (
              this.getDataUrl.includes('practice-log') ||
              this.getDataUrl.includes('PracticeLog')
            ) {
              response.data = response.data.map((item) => {
                if (
                  item.message &&
                  typeof item.message === 'string' &&
                  item.message.includes('No such host is known')
                ) {
                  return {
                    ...item,
                    resolution:
                      'Internet connectivity is poor or unavailable.',
                  };
                }
                return item;
              });
            }

            if (this.gridConfig?.showAllPagination === false) {
              this.pageSizeOptions.pop();
            }

            if (!this.appliedFilter) {
              localStorage.setItem(
                'choosenFilters',
                JSON.stringify(this.filters)
              );
            }

            if (
              this.uniqueTableName === 'LoadSchedule' ||
              this.uniqueTableName === 'PracticeLoadConfiguration'
            ) {
              response.data.length > 0 &&
                response.data.map((e) => {
                  e.description = this.getCronDescription(
                    e.startCronExpression
                  );
                });
            }

            //for release notes use <br> instead of \n
            if (this.uniqueTableName === 'PMSAgentRelease' || this.uniqueTableName === 'PMSAgentReleaseMapping') {
              response.data.length > 0 &&
                response.data.map((e) => {
                  e.releaseNote = e.releaseNote.replace(/\n/g, '<br>');
                  if (this.uniqueTableName === 'PMSAgentReleaseMapping' && this.installerUrl) {
                    const baseUrl = this.installerUrl.substring(0, this.installerUrl.lastIndexOf('/') + 1);
                    e.downloadLink = e.version ? `${baseUrl}PMSAgent_${e.version}.exe` : this.installerUrl;
                  }
                });
            }

            let rawData = response?.data?.historyData || [];
            if (this.uniqueTableName === 'AuditReport') {
              if (rawData.length > 0) {
                const parentKeyMap = new Map<string, number>();
                let currentGroupIndex = 0;

                for (let row of rawData) {
                  const key = row.parentKey;
                  if (!parentKeyMap.has(key)) {
                    currentGroupIndex++;
                    parentKeyMap.set(key, currentGroupIndex);
                  }
                  (row as any).groupIndex = parentKeyMap.get(key);
                }

                const logDates = response?.data?.logDates;
                const minDate = new Date(Date.parse(logDates.startDate + 'Z'));
                const maxDate = new Date(Date.parse(logDates.endDate + 'Z'));

                // Log as plain date strings
                this.customDateRange = {
                  from: this.extractDateFromISOString(minDate.toISOString()),
                  to: this.extractDateFromISOString(maxDate.toISOString())
                };

                this.showDateFilterDropdown = true;
                this.minFromDate = this.customDateRange.from;
                this.maxToDate = this.customDateRange.to;
                // this.getDateFilterLabel(); // KW
                this.isDateInputDisabled = false;
              } else {
                this.showDateFilterDropdown = false;
                this.isDateInputDisabled = true;
              }

              // Normalize audit API payload for the shared grid table binding.
              response.data = rawData;
            }

            this._totalRecordsCount = response.totalRecordsCount;

            this.totalItems = response.totalRecordsCount;
            this.paginator.length = this.totalItems;

            this.dataSource.data = [...response.data];
            // Check if data is empty
            if (response.data.length === 0) {
              this.emptyDataMessage =
                'There are no items to display at the moment.';
            } else {
              this.emptyDataMessage = '';
            }

            // Calculate low resource count for practice pages
            if (this.isPracticePage()) {
              this.calculateLowResourceCount();
            }

            this.isLoading = false;

            this.changeDetectorRef.detectChanges();
          });
        });

      this.activeDataFetchSubscription = subscription;

    } catch (error) {
      this.zone.run(() => {
        this.handleError(error);
        this.isLoading = false;
        this.showBlockingLoader = false;
        this.changeDetectorRef.detectChanges();
      });
    }
  }

  handleError(error: any): void {
    this.showErrorCard = true;
    this.isLoading = false;

    if (error.error && error.error.errorMessage) {
      this.errorTitle = error.error.errorMessage;
      this.errorMessage =
        error.error.detailedErrorMessage ||
        'No detailed error message provided.';
    } else if (error.status === 408) {
      this.errorTitle = 'Request Timeout';
      this.errorMessage =
        'The server is taking too long to respond. Please check your internet connection and try again.';
    } else if (error.status === 403) {
      this.errorTitle = 'Forbidden';
      this.errorMessage =
        "You don't have permission to access this information. If you believe this is a mistake, please contact support.";
    } else if (error.status === 404) {
      this.errorTitle = 'Not Found';
      this.errorMessage =
        'The requested information could not be found. It may have been moved or deleted.';
    } else if (error.status >= 500) {
      this.errorTitle = 'Internal Server Error';
      this.errorMessage =
        'There was a problem with the server. Please try again later or contact support if the problem persists.';
    } else {
      this.errorTitle = error.error?.errorMessage || 'Unexpected Error';
      this.errorMessage = error.error?.detailedErrorMessage ||
        'An unexpected error occurred. Please try again or contact support if the problem continues.';
    }

    // console.error('Error occurred:', error);
    this.changeDetectorRef.detectChanges();
  }

  extractDateFromISOString(dateTimeStr: string): string {
    if (!dateTimeStr) return '';
    return new Date(dateTimeStr).toISOString().split('T')[0];
  }

  onCustomDateChange(filterConfig: FilterConfig): void {
    const from = new Date(this.customDateRange.from);
    const to = new Date(this.customDateRange.to);

    // Optional: prevent future dates or invalid ranges
    if (from && to && from <= to) {
      this.filters[filterConfig.jsonKey] = {
        startDate: from.toISOString(),
        endDate: to.toISOString(),
      };
    }
  }

  onReloadAfterError() {
    this.closeErrorCard();
    this.fetchData();
  }

  closeErrorCard() {
    this.showErrorCard = false;
    this.changeDetectorRef.detectChanges();
  }

  onFilterChange(filterKey: string, selectedOptions: any[]): void {
    this.filters[filterKey] = selectedOptions;
    this.pageNumber = 1;
    this.fetchData();
  }

  toggleFilterDropdown(filterKey: string, event: Event): void {
    for (let key in this.isFilterDropdownOpen) {
      if (filterKey !== key) {
        this.isFilterDropdownOpen[key] = false;
      }
    }
    this.isFilterDropdownOpen[filterKey] =
      !this.isFilterDropdownOpen[filterKey];
  }

  onFilterOptionClick(filter: FilterConfig, option: any): void {
    if (filter.type === 'single') {
      this.filters[filter.jsonKey] = option.id;
    } else if (filter.type === 'multi') {
      if (!this.filters[filter.jsonKey]) {
        this.filters[filter.jsonKey] = [];
      }
      const index = this.filters[filter.jsonKey].indexOf(option);
      if (index === -1) {
        this.filters[filter.jsonKey].push(option);
      } else {
        this.filters[filter.jsonKey].splice(index, 1);
      }
    }
    this.pageNumber = 1;
  }

  isFilterOptionSelected(filterConfig: FilterConfig, option: any): boolean {
    const selected = this.filters[filterConfig.jsonKey];

    // Filters not yet initialized — fall back to checking the options' default checked state
    if (!selected) {
      const options = this.getMultiFilterOptions(filterConfig);
      const allOption = options.find((o: any) => o.id === 'All');
      // If All is checked by default, every option should appear selected
      if (allOption?.checked === true) {
        return true;
      }
      return option.checked === true;
    }

    return selected.some((selectedOption: any) => selectedOption.id === option.id);
  }

  getDateFilterOptions(
    filterConfig: FilterConfig
  ): { label: string; value: string }[] {
    return [
      { label: 'Last 24 hours', value: 'last24hours' },
      { label: 'Last 7 days', value: 'last7days' },
      { label: 'Last 30 days', value: 'last30days' },
      { label: 'Custom', value: 'custom' },
    ];
  }

  onDateFilterOptionChange(
    filterConfig: FilterConfig,
    selectedOption: string
  ): void {
    if (selectedOption === 'custom') {
      const currentDateTime = new Date();
      this.customDateRange.from = this.formatDateTime(currentDateTime);
      this.customDateRange.to = this.formatDateTime(currentDateTime);
      this.filters[filterConfig.jsonKey] = selectedOption;
      this.isSelected = 'custom';
    } else {
      const currentDate = new Date();
      let startDate: Date;

      switch (selectedOption) {
        case 'last24hours':
          startDate = new Date(currentDate.getTime() - 24 * 60 * 60 * 1000);
          this.isSelected = 'last24hours';
          break;
        case 'last7days':
          startDate = new Date(currentDate.getTime() - 7 * 24 * 60 * 60 * 1000);
          this.isSelected = 'last7days';
          break;
        case 'last30days':
          startDate = new Date(
            currentDate.getTime() - 30 * 24 * 60 * 60 * 1000
          );
          this.isSelected = 'last30days';
          break;
        default:
          startDate = currentDate;
      }

      this.filters[filterConfig.jsonKey] = {
        startDate: startDate.toISOString(),
        endDate: currentDate.toISOString(),
      };

      this.pageNumber = 1;
    }
  }

  hasFilterOptionsSelected(filterConfig: FilterConfig): boolean {
    if (filterConfig.type === 'single') {
      return !!this.filters[filterConfig.jsonKey];
    } else if (filterConfig.type === 'multi') {
      return !!this.filters[filterConfig.jsonKey]?.length;
    } else if (filterConfig.type === 'date') {
      return !!this.filters[filterConfig.jsonKey];
    }
    return false;
  }

  getAppliedFilterCount(filterConfig: FilterConfig): number {
    if (filterConfig.type === 'single') {
      return this.filters[filterConfig.jsonKey] ? 1 : 0;
    } else if (filterConfig.type === 'multi') {
      return (this.filters[filterConfig.jsonKey] || []).filter((o: any) => o.id !== 'All').length;
    } else if (filterConfig.type === 'date') {
      return this.filters[filterConfig.jsonKey] ? 1 : 0;
    }
    return 0;
  }

  getDateFilterLabel(filterConfig: FilterConfig): string {
    if (!this.filters[filterConfig.jsonKey]) {
      return '';
    }
    if (typeof this.filters[filterConfig.jsonKey] === 'string') {
      switch (this.isSelected) {
        case 'last24hours':
          return 'Last 24 hours';
        case 'last7days':
          return 'Last 7 days';
        case 'last30days':
          return 'Last 30 days';
        case 'custom':
          const from = new Date(this.customDateRange.from);
          const to = new Date(this.customDateRange.to);
          const diffDays = Math.ceil(
            (to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)
          );
          return `Last ${diffDays} days`;
      }
    } else if (typeof this.filters[filterConfig.jsonKey] === 'object') {
      const from = new Date(this.filters[filterConfig.jsonKey].startDate);
      const to = new Date(this.filters[filterConfig.jsonKey].endDate);
      const diffDays = Math.ceil(
        (to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)
      );
      return `Last ${diffDays} days`;
    } else if (this.customDateRange.from && this.customDateRange.to) {
      const from = new Date(this.customDateRange.from);
      const to = new Date(this.customDateRange.to);
      const diffDays = Math.ceil(
        (to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)
      );
      return `Last ${diffDays} days`;
    }

    return '';
  }

  setupColumns(): void {
    this.displayedColumns =
      this.gridConfig.editable || this.gridConfig.deletable
        ? ['action', ...this.gridConfig.columns.map((c) => c.jsonKey)]
        : this.gridConfig.columns.map((c) => c.jsonKey);
  }

  applyFilter(filterValue: string) {
    const searchQuery = filterValue.trim().toLowerCase();
    this.searchSubject.next(searchQuery);
  }

  onFilterOptionChange(
    filterConfig: FilterConfig,
    option: any,
    checked: boolean
  ): void {
    if (filterConfig.type !== 'multi') {
      return;
    }

    this.ensureFilterSelectionInitialized(filterConfig);
    const filterKey = filterConfig.jsonKey;
    const options = this.getMultiFilterOptions(filterConfig);
    const allOption = options.find((item: any) => item.id === 'All');

    if (option.id === 'All') {
      this.filters[filterKey] = checked ? [...options] : [];
      return;
    }

    if (checked) {
      const alreadySelected = this.filters[filterKey].some(
        (selectedOption: any) => selectedOption.id === option.id
      );
      if (!alreadySelected) {
        this.filters[filterKey].push(option);
      }
    } else {
      const index = this.filters[filterKey].findIndex(
        (selectedOption: any) => selectedOption.id === option.id
      );
      if (index !== -1) {
        this.filters[filterKey].splice(index, 1);
      }

      if (allOption) {
        this.filters[filterKey] = this.filters[filterKey].filter(
          (selectedOption: any) => selectedOption.id !== allOption.id
        );
      }
    }

    this.syncAllOptionForMultiFilter(filterConfig);
  }

  private initializeDefaultFilterSelections(): void {
    if (!this.gridConfig?.filters?.length) {
      return;
    }

    for (const filterConfig of this.gridConfig.filters) {
      if (filterConfig.type !== 'multi') {
        continue;
      }

      if (this.filters[filterConfig.jsonKey] !== undefined) {
        continue;
      }

      const defaultSelectedOptions = this.getDefaultSelectedOptions(filterConfig);
      if (defaultSelectedOptions.length > 0) {
        this.filters[filterConfig.jsonKey] = defaultSelectedOptions;
      }
    }
  }

  private getMultiFilterOptions(filterConfig: FilterConfig): any[] {
    return filterConfig?.options?.data || [];
  }

  private getDefaultSelectedOptions(filterConfig: FilterConfig): any[] {
    const options = this.getMultiFilterOptions(filterConfig);
    const checkedOptions = options.filter((option: any) => option.checked);
    const hasAllChecked = checkedOptions.some(
      (checkedOption: any) => checkedOption.id === 'All'
    );

    if (hasAllChecked) {
      return [...options];
    }

    return checkedOptions;
  }

  private ensureFilterSelectionInitialized(filterConfig: FilterConfig): void {
    const filterKey = filterConfig.jsonKey;
    if (this.filters[filterKey]) {
      return;
    }

    this.filters[filterKey] = this.getDefaultSelectedOptions(filterConfig);
  }

  private syncAllOptionForMultiFilter(filterConfig: FilterConfig): void {
    const filterKey = filterConfig.jsonKey;
    const options = this.getMultiFilterOptions(filterConfig);
    const allOption = options.find((item: any) => item.id === 'All');

    if (!allOption) {
      return;
    }

    const nonAllOptions = options.filter((item: any) => item.id !== 'All');
    const selectedIds = new Set(
      (this.filters[filterKey] || []).map((item: any) => item.id)
    );
    const areAllNonAllSelected = nonAllOptions.every((item: any) =>
      selectedIds.has(item.id)
    );

    if (areAllNonAllSelected) {
      if (!selectedIds.has(allOption.id)) {
        this.filters[filterKey].push(allOption);
      }
      return;
    }

    this.filters[filterKey] = (this.filters[filterKey] || []).filter(
      (item: any) => item.id !== allOption.id
    );
  }

  formatDateTime(date: string | Date) {
    if (date == null || date == '')
      return '';
    const userTimeZone = moment.tz.guess();
    const userDateTime = moment.utc(date).tz(userTimeZone);
    return userDateTime.format('YYYY-MM-DD HH:mm');
  }

  applyFilters(filterConfig): void {
    localStorage.setItem('choosenFilters', JSON.stringify(this.filters));

    this.appliedFilter = true;

    // Force change detection immediately after setting appliedFilter
    this.changeDetectorRef.detectChanges();

    if (this.filters[filterConfig.jsonKey] === 'custom') {
      const fromDate = new Date(this.customDateRange.from);
      const toDate = new Date(this.customDateRange.to);

      toDate.setHours(23, 59, 59, 999);

      this.filters[filterConfig.jsonKey] = {
        startDate: fromDate.toISOString(),
        endDate: toDate.toISOString(),
      };
    } else if (typeof this.filters[filterConfig.jsonKey] === 'object') {
      this.customDateRange.from = this.filters[filterConfig.jsonKey].startDate;
      this.customDateRange.to = this.filters[filterConfig.jsonKey].endDate;
    }

    this.pageNumber = 1;
    this.fetchData();
    this.closeFilterDropdowns();
    this.changeDetectorRef.detectChanges();
  }

  clearFilters(filterConfig: FilterConfig): void {
    this.filters[filterConfig.jsonKey] = [];
    this.closeFilterDropdowns();
  }

  cancelFilters(filterConfig: FilterConfig): void {
    if (this.appliedFilter) {
      let filtersFromStorage = localStorage.getItem('choosenFilters');
      let convertedFilter = JSON.parse(filtersFromStorage);
      this.filters = convertedFilter;
    } else {
      this.filters = {};
      localStorage.setItem('choosenFilters', JSON.stringify(this.filters));
    }
    this.closeFilterDropdowns();
  }

  closeFilterDropdowns(): void {
    Object.keys(this.isFilterDropdownOpen).forEach((key) => {
      this.isFilterDropdownOpen[key] = false;
    });
  }

  isAllOptionsSelected(filterConfig: FilterConfig): boolean {
    return (
      filterConfig.options?.data?.length ===
      this.filters[filterConfig.jsonKey]?.length
    );
  }

  onAllOptionsChange(filterConfig: FilterConfig, checked: boolean): void {
    if (checked) {
      this.filters[filterConfig.jsonKey] = filterConfig.options?.data.map(
        (option: any) => option
      );
    } else {
      this.filters[filterConfig.jsonKey] = [];
    }
    this.pageNumber = 1;
  }

  getSelectedDate(filterConfig: FilterConfig): Date | null {
    return this.filters[filterConfig.jsonKey]
      ? new Date(this.filters[filterConfig.jsonKey])
      : null;
  }

  onDateChange(filterConfig: FilterConfig, selectedDate: string): void {
    this.filters[filterConfig.jsonKey] = selectedDate;
  }

  isFilterApplied(filterConfig: FilterConfig): boolean {
    return (
      this.filters[filterConfig.jsonKey] &&
      this.filters[filterConfig.jsonKey].length > 0
    );
  }

  getSelectedOptionName(filterConfig: FilterConfig): string {
    const selectedOption = filterConfig.options?.data.find(
      (option: any) => option.id === this.filters[filterConfig.jsonKey]
    );
    return selectedOption ? selectedOption.name : '';
  }

  getSelectedOptionNames(filterConfig: FilterConfig): string {
    if (this.isAllOptionsSelected(filterConfig)) {
      return 'All';
    }
    const selectedOptions = filterConfig.options?.data?.filter((option: any) =>
      this.filters[filterConfig.jsonKey]?.some(
        (selectedOption: any) => selectedOption.id === option.id
      )
    );
    const selectedOptionNames =
      selectedOptions?.map((option: any) => option.name) || [];
    return selectedOptionNames.length > 1
      ? `${selectedOptionNames[0]}...`
      : selectedOptionNames[0] || '';
  }

  ngAfterViewInit() {
    this.paginator.pageSize = this.pageSize;
    this.sort.active = this.sortBy;
    this.sort.direction = this.sortOrder as SortDirection;
    this.sort.start = this.sortOrder as SortDirection;
    this.dataSource.sort = this.sort;
    this.sort.sortChange.emit();
    this.changeDetectorRef.detectChanges();
    this.paginator.page.subscribe((event: PageEvent) => {
      this.paginator.length = this.totalItems;
      this.pageNumber = event.pageIndex + 1;
      this.pageSize = event.pageSize;
      this.fetchData();
    });
    this.sort.sortChange.subscribe(() => {
      this.sortBy = this.sort.active;
      this.sortOrder = this.sort.direction;
      this.pageNumber = 1;
      this.fetchData();
      this.paginator.length = this.totalItems;
    });
  }

  onAddClick(): void {
    this.addClicked.emit(true);
  }

  onEditClick(rowData: any): void {
    this.editClicked.emit({ ...rowData });
  }

  onEdit2Click(rowData: any): void {
    this.edit2Clicked.emit({ ...rowData });
  }

  onToggleChange(element: any, jsonKey: string, toggleValue: boolean): void {
    const toggleUrl = this.gridConfig.columns.find(
      (column) => column.jsonKey === jsonKey
    )?.toggleUrl;

    const patchUrl = toggleUrl
      ? toggleUrl.replace(':id', element.id.toString())
      : this.toggleUrl;

    const notes = this.isUserManagementPage() && jsonKey === 'isActive'
      ? (prompt('Notes (optional): Why is this user being enabled/disabled?') ?? undefined)
      : undefined;

    const subscription = this.gridDataService
      .toggleData(patchUrl, element.id, jsonKey, toggleValue, notes)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response) => {
          element[jsonKey] = response[jsonKey];
          const index = this.dataSource.data.findIndex(
            (item: any) => item.id === element.id
          );
          if (index !== -1) {
            this.dataSource.data[index][jsonKey] = response[jsonKey];
          }
          this.onToggleShowToastEmitter.emit({
            severity: 'success',
            title: 'Success',
            message: response?.data,
          });
        },
        (error) => {
          console.error('Error updating toggle value:', error);
          this.onToggleShowToastEmitter.emit({
            severity: 'error',
            title: 'Error',
            message: error?.error?.errorMessage,
          });
        }
      );

    this.subscriptions.push(subscription);
  }

  toggleDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  resetFilter() {
    this.filters = this.isPracticePage()
      ? {}
      : { ...(this.gridConfig?.defaultFilters ?? {}) };
    if (this.isConfigurationPage()) {
      delete this.filters['isActive'];
    }
    this.initializeDefaultFilterSelections();
    this.searchQuery = '';
    this.searchSubject.next('');
    this.resetAll = true;
    localStorage.removeItem('choosenFilters');
    this.appliedFilter = false;
    this.isSelected = undefined;
    this.customDateRange = { from: '', to: '' };
    this.pageNumber = 1;
    this.closeFilterDropdowns();

    if (this.isPracticePage()) {
      this.selectedViewOption = 'all';
    }

    if (this.isConfigurationPage()) {
      this.selectedConfigViewOption = 'all';
    }

    this.fetchData();
    this.changeDetectorRef.detectChanges();
  }

  hasActiveFilters(): boolean {
    if (this.searchQuery?.trim().length > 0) {
      return true;
    }

    if (this.appliedFilter) {
      return true;
    }

    // Ignore keys that are set by defaultFilters — those are not user-applied
    const defaultKeys = new Set(Object.keys(this.gridConfig?.defaultFilters ?? {}));
    const userFilters = Object.keys(this.filters).filter(k => !defaultKeys.has(k));
    if (userFilters.length > 0) {
      return true;
    }

    if (this.gridConfig.filters?.some(filterConfig => this.hasFilterOptionsSelected(filterConfig))) {
      return true;
    }

    return false;
  }

  hasDefaultActiveFilter(): boolean {
    return this.gridConfig?.defaultFilters?.hasOwnProperty('isActive') === true;
  }

  getActiveFilterLabel(): string {
    const val = this.filters['isActive'];
    if (val === true) return 'Active';
    if (val === false) return 'Inactive';
    return 'All';
  }

  setActiveFilter(value: boolean | null): void {
    if (value === null) {
      delete this.filters['isActive'];
    } else {
      this.filters['isActive'] = value;
    }
    this.pageNumber = 1;
    this.fetchData();
  }

  hasFiltersInObject(): boolean {
    return Object.keys(this.filters).length > 0;
  }

  async exportData(): Promise<void> {
    this.isExporting = true;
    this.changeDetectorRef.detectChanges();

    try {
      let data = this.dataSource.data;

      if (data === undefined || data === null || data.length === 0) {
        this.isColumnSelectorOpen = false;
        this.isSavingPreferences = false;
        this.isDropdownOpen = false;
        this.isLoading = false;
        this.isExporting = false;
        this.changeDetectorRef.detectChanges();
        const toast: Toast = {
          severity: 'warn',
          title: 'Warning',
          message: 'No data available to export.',
          duration: 3000
        };
        this.onToggleShowToastEmitter.emit(toast);
        this.changeDetectorRef.markForCheck();
        return;
      }

      // Filter out current user and super admin for user management exports
      if (this.uniqueTableName === 'UserRoleManagement') {
        data = data.filter(
          (item: any) => (item.email != this.userEmail) && (item.email != 'pms_admin@dentalcorp.ca')
        );
      }

      const columns = this.getExportColumns();
      const headers = columns.map(column => column.label.toUpperCase());

      const jsonData = data.map((row: any) => {
        if (
          this.uniqueTableName === 'LoadSchedule' ||
          this.uniqueTableName === 'PracticeLoadConfiguration'
        ) {
          row = { ...row };
          if (row.startCronExpression) {
            row.description = this.getCronDescription(row.startCronExpression);
          }
        }

        const rowData: any = {};
        columns.forEach((column) => {
          let value = row[column.jsonKey];
          if (column.jsonKey === 'releaseNote' && typeof value === 'string') {
            value = value.replace(/<br>/gi, '\n');
          }
          if (typeof value === 'boolean') {
            rowData[column.label.toUpperCase()] = value ? 'Yes' : 'No';
          } else if (column.jsonKey === 'isActive' || column.jsonKey === 'isLive' || column.jsonKey === 'isInstalled' || column.jsonKey === 'transformStatus' || column.jsonKey === 'isHyperV') {
            rowData[column.label.toUpperCase()] = (value === null || value === undefined) ? 'No' : (value ? 'Yes' : 'No');
          } else if (column.dataType === 'date') {
            if (value) {
              const formattedDate = moment
                .utc(value, 'YYYY-MM-DD HH:mm:ss')
                .local()
                .format('YYYY-MM-DD HH:mm:ss');
              rowData[column.label.toUpperCase()] = formattedDate;
            }
          } else {
            rowData[column.label.toUpperCase()] = value;
          }
        });
        return rowData;
      });

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Sheet1');

      worksheet.addRow(headers);
      jsonData.forEach(row => {
        const rowValues = headers.map(header => row[header]);
        const addedRow = worksheet.addRow(rowValues);
        const releaseNoteColIndex = columns.findIndex(c => c.jsonKey === 'releaseNote');
        if (releaseNoteColIndex !== -1) {
          const cell = addedRow.getCell(releaseNoteColIndex + 1);
          cell.alignment = { wrapText: true };
        }
      });

      columns.forEach((_, index) => {
        worksheet.getColumn(index + 1).width = 20;
      });

      const currentURLSegements = this._router.url.split('/');
      const currentPath = currentURLSegements[currentURLSegements.length - 1];
      const fileName = this.generateFilename(
        BREADCRUMB_NAMES[currentPath] || 'Export',
        'xlsx'
      );

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, fileName);

      this.isDropdownOpen = false;
      this.isExporting = false;
      this.changeDetectorRef.detectChanges();
    } catch (error) {
      console.error('Error exporting data:', error);
      this.isExporting = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  onDelete(element: any): void {
    this.deleteClicked.emit(element);
  }

  generateFilename(name: string, format: string) {
    const today = moment();
    name = (name || 'Export').replace(' ', '_');
    // corner case for assign and configure release
    name =
      name.includes('Assign') || name.includes('Configure')
        ? name + '_Release'
        : name;
    const dateString = today.format('YYYY-MM-DD-HH-mm-ss');
    // Concatenate the date with the name
    return `${name}_${dateString}.${format}`;
  }

  rowClick(data, event) {
    this.onRowClick.emit(data);
  }

  openLink(url: string): void {
    window.open(url, '_blank');
  }

  addAllOption() {
    if (!(this.gridConfig?.showAllPagination === false)) {
      const lastOption = document.querySelector(
        '.mat-mdc-select-panel-above .mat-mdc-option:last-child > span'
      );
      lastOption ? (lastOption.innerHTML = 'All') : null;
    }
  }

  getPageSize() {
    this.gridConfig.showAllPagination == false
      ? this.pageSizeOptions.pop()
      : null;
    return this.pageSizeOptions;
  }

  private configureMarked(): void {
    const renderer = new marked.Renderer();
    renderer.link = ({ href, title, text }) => {
      const titleAttr = title ? ` title="${title}"` : '';
      return `<a href="${href}" target="_blank" rel="noopener noreferrer"${titleAttr}>${text}</a>`;
    };
    marked.setOptions({ renderer });
  }

  getMakrDownText(isMarkDownSupported: boolean, text: string) {
    return isMarkDownSupported ? marked(text) : '';
  }

  disableSuccessAcknowldgeToggle(element: any): boolean {
    if (element.pmsStatusCode) {
      return ACKNOWLEDGEABLE_SUCCESS_STATUS_CODES.includes(
        element.pmsStatusCode
      )
        ? true
        : false;
    }
    return false;
  }

  disableEditable2(element: any): boolean {
    const conditionMap = this.gridConfig?.disableEdit2Condition;
    if (!conditionMap) return false;
    for (const key in conditionMap) {
      if (conditionMap.hasOwnProperty(key)) {
        const disallowedValues = conditionMap[key];
        const elementValue = (element[key] ?? '').toString().toLowerCase();
        if (disallowedValues.some((val: string) => val.toString().toLowerCase() === elementValue)) {
          return true;
        }
      }
    }
    return false;
  }

  rowColorClass(element: any): string {
    const conditionMap = this.gridConfig?.rowColorCondition;
    if (!conditionMap) return '';

    const color = conditionMap.color;
    const conditionCopy: any = { ...conditionMap };
    delete conditionCopy.color;

    for (const key in conditionCopy) {
      if (conditionCopy.hasOwnProperty(key)) {
        const expectedValues = Array.isArray(conditionCopy[key])
          ? conditionCopy[key]
          : [conditionCopy[key]];

        const elementValue = element[key];

        // strict equality check
        if (expectedValues.some((val: any) => (val == elementValue))) {
          switch (color?.toLowerCase()) {
            case 'red':
              return '!bg-red-600';
            case 'green':
              return '!bg-green-600';
            case 'yellow':
              return '!bg-yellow-600';
            default:
              return '';
          }
        }
      }
    }
    return '';
  }

  isErrorLog(element) {
    // Use the provided isErrorLog function if available
    if (typeof this.customIsErrorLog === 'function') {
      return this.customIsErrorLog(element);
    }

    // Otherwise use the default implementation
    if (element.pmsStatusCode) {
      return ACKNOWLEDGEABLE_ERROR_STATUS_CODES.includes(element.pmsStatusCode)
        ? true
        : false;
    }
    return false;
  }

  getCronDescription(cronExpression: string) {
    let translatedCRON = '';
    try {
      translatedCRON = cronstrue.toString(cronExpression);
    } catch {
      translatedCRON = 'Invalid CRON Expression';
    }
    return translatedCRON;
  }

  async exportAllData(): Promise<void> {
    this.isExporting = true;
    this.changeDetectorRef.detectChanges();

    try {
      const exportFilters = this.getExportFilters();
      const response = await this.gridDataService
        .fetchData(
          this.getDataUrl,
          1,
          MAX_INT_32,
          '',
          this.sortBy,
          this.sortOrder,
          exportFilters
        )
        .toPromise();

      if (response.isSuccess === false) {
        this.handleError({
          status: response.statusCode,
          error: {
            errorMessage: response.errorMessage,
            detailedErrorMessage: response.detailedErrorMessage,
          },
        });
        this.isExporting = false;
        this.changeDetectorRef.detectChanges();
        return;
      }

      let allData = this.extractRowsFromResponse(response);

      if (allData === undefined || allData === null || allData.length === 0) {
        this.isColumnSelectorOpen = false;
        this.isSavingPreferences = false;
        this.isDropdownOpen = false;
        this.isLoading = false;
        this.isExporting = false;
        this.changeDetectorRef.detectChanges();
        const toast: Toast = {
          severity: 'warn',
          title: 'Warning',
          message: 'No data available to export.',
          duration: 3000
        };
        this.onToggleShowToastEmitter.emit(toast);
        this.changeDetectorRef.markForCheck();
        return;
      }

      // Filter out current user and super admin for user management exports
      if (this.uniqueTableName === 'UserRoleManagement') {
        allData = allData.filter(
          (item: any) => (item.email != this.userEmail) && (item.email != 'pms_admin@dentalcorp.ca')
        );
      }
      const columns = this.getExportColumns();
      const headers = columns.map(column => column.label.toUpperCase());

      const jsonData = allData.map((row: any) => {
        if (this.uniqueTableName === 'LoadSchedule' || this.uniqueTableName === 'PracticeLoadConfiguration') {
          row = { ...row };
          if (row.startCronExpression) {
            row.description = this.getCronDescription(row.startCronExpression);
          }
        }

        const rowData: any = {};
        columns.forEach((column) => {
          let value = row[column.jsonKey];
          if (column.jsonKey === 'releaseNote' && typeof value === 'string') {
            value = value.replace(/<br>/gi, '\n');
          }
          if (typeof value === 'boolean') {
            rowData[column.label.toUpperCase()] = value ? 'Yes' : 'No';
          } else if (column.jsonKey === 'isActive' || column.jsonKey === 'isLive' || column.jsonKey === 'isInstalled' || column.jsonKey === 'transformStatus' || column.jsonKey === 'isHyperV') {
            rowData[column.label.toUpperCase()] = (value === null || value === undefined) ? 'No' : (value ? 'Yes' : 'No');
          } else if (column.dataType === 'date') {
            if (value) {
              const formattedDate = moment
                .utc(value, 'YYYY-MM-DD HH:mm:ss')
                .local()
                .format('YYYY-MM-DD HH:mm:ss');
              rowData[column.label.toUpperCase()] = formattedDate;
            }
          } else {
            rowData[column.label.toUpperCase()] = value;
          }
        });
        return rowData;
      });

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Sheet1');

      worksheet.addRow(headers);
      jsonData.forEach(row => {
        const rowValues = headers.map(header => row[header]);
        const addedRow = worksheet.addRow(rowValues);
        const releaseNoteColIndex = columns.findIndex(c => c.jsonKey === 'releaseNote');
        if (releaseNoteColIndex !== -1) {
          const cell = addedRow.getCell(releaseNoteColIndex + 1);
          cell.alignment = { wrapText: true };
        }
      });

      columns.forEach((_, index) => {
        worksheet.getColumn(index + 1).width = 20;
      });

      const currentURLSegements = this._router.url.split('/');
      const currentPath = currentURLSegements[currentURLSegements.length - 1];
      const fileName = this.generateFilename(
        'All_' + (BREADCRUMB_NAMES[currentPath] || 'Export'),
        'xlsx'
      );

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, fileName);

      this.isDropdownOpen = false;
      this.isExporting = false;
      this.changeDetectorRef.detectChanges();
    } catch (error) {
      this.isExporting = false;
      this.changeDetectorRef.detectChanges();
      this.handleError(error);
    }
  }

  // Export data to PDF
  async exportDataToPDF(reportTitle: string, reportType: string): Promise<void> {
    let data: any[] = [];
    switch (reportType) {
      case 'Current':
        data = this.dataSource.data;
        break;
      case 'All':
        this.isLoading = true;
        try {
          const exportFilters = this.getExportFilters();
          // API Call - fetch all rows
          const response = await this.gridDataService
            .fetchData(
              this.getDataUrl,
              1,
              MAX_INT_32,
              '',
              this.sortBy,
              this.sortOrder,
              exportFilters
            )
            .toPromise();

          if (response.isSuccess === false) {
            this.isDropdownOpen = false;
            this.isLoading = false;
            this.changeDetectorRef.detectChanges();
            this.handleError({
              status: response.statusCode,
              error: {
                errorMessage: response.errorMessage,
                detailedErrorMessage: response.detailedErrorMessage,
              },
            });
            return;
          }

          // Set data
          data = this.extractRowsFromResponse(response);
        } catch (error) {
          this.isDropdownOpen = false;
          this.isLoading = false;
          this.changeDetectorRef.detectChanges();
          this.handleError(error);
        }
        break;
      default:
        data = this.dataSource.data;
        break;
    }

    if (data === undefined || data === null || data.length === 0) {
      this.isColumnSelectorOpen = false;
      this.isSavingPreferences = false;
      this.isDropdownOpen = false;
      this.isLoading = false;
      this.changeDetectorRef.detectChanges();
      const toast: Toast = {
        severity: 'warn',
        title: 'Warning',
        message: 'No data available to export.',
        duration: 3000
      };
      this.onToggleShowToastEmitter.emit(toast);
      this.changeDetectorRef.markForCheck();
      return;
    }

    // Regenerate cron description for LoadSchedule and PracticeLoadConfiguration
    if (this.uniqueTableName === 'LoadSchedule' || this.uniqueTableName === 'PracticeLoadConfiguration') {
      data = data.map((row: any) => {
        const r = { ...row };
        if (r.startCronExpression) {
          r.description = this.getCronDescription(r.startCronExpression);
        }
        return r;
      });
    }

    // Special handling for practice operational hours data
    if (this.uniqueTableName === 'PracticeOperationalHours') {
      data.forEach((row: any) => {
        // Ensure day is properly formatted
        if (typeof row.day === 'number') {
          const dayNames = { 1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 4: 'Thursday', 5: 'Friday', 6: 'Saturday', 7: 'Sunday' };
          row.dayName = dayNames[row.day] || 'Unknown';
        }
        // Format time fields to remove seconds
        if (row.openingTime && row.openingTime.includes(':')) {
          const timeParts = row.openingTime.split(':');
          row.openingTime = `${timeParts[0]}:${timeParts[1]}`;
        }
        if (row.closingTime && row.closingTime.includes(':')) {
          const timeParts = row.closingTime.split(':');
          row.closingTime = `${timeParts[0]}:${timeParts[1]}`;
        }
      });
    }

    data.forEach((row: any) => {
      this.gridConfig.columns.forEach((column) => {
        if (!this.visibleColumns.has(column.jsonKey)) {
          return; // Skip hidden columns
        }
        const value = row[column.jsonKey];
        if (typeof value === 'boolean') {
          // Convert boolean to Yes/No
          row[column.jsonKey] = value ? 'Yes' : 'No';

        } else if (column.dataType === 'date' || column.dataType === 'dateOnly') {
          // Format date if present
          if (value) {
            const formattedDate = moment
              .utc(value, 'YYYY-MM-DD HH:mm:ss') // Use correct tokens
              .local()
              .format('YYYY-MM-DD HH:mm:ss');
            row[column.jsonKey] = formattedDate;
          } else {
            row[column.jsonKey] = '';
          }

        } else {
          // Default: keep value or empty string
          row[column.jsonKey] = value ?? '';
        }
      });
    });

    this.isLoading = false;
    this.isDropdownOpen = false;
    this.changeDetectorRef.detectChanges();

    const currentURLSegements = this._router.url.split('/');
    const currentPath = currentURLSegements[currentURLSegements.length - 1];
    const fileName = this.generateFilename(
      BREADCRUMB_NAMES[currentPath] || 'Export',
      'pdf'
    );

    // Call the PDF export function with prepared data
    const visibleColConfigs = this.gridConfig.columns.filter(col => this.visibleColumns.has(col.jsonKey));
    const pdfHeaders = visibleColConfigs.map(c => c.label.toUpperCase());
    const pdfFields = visibleColConfigs.map(c => c.jsonKey);
    const previewPageSize = 'A3';
    const previewOrientation = 'landscape';
    const previewMargins: [number, number, number, number] = [40, 60, 40, 40];
    const { fontSize: previewFontSize, pageSize: previewFittedPage } = computeFitSettings(
      pdfHeaders, pdfFields, data, previewPageSize, previewOrientation, previewMargins,
      this.visibleColumns !== null && this.visibleColumns.size > 10 ? 8 : 10,
      this.visibleColumns !== null && this.visibleColumns.size > 10 ? 6 : 8,
      2
    );
    const previewInnerWidth = innerWidth(previewFittedPage as any, previewOrientation, previewMargins[0], previewMargins[2]);
    const previewRequiredWidth = pdfFields.reduce((sum, f, i) =>
      sum + estimateColumnWidth(pdfHeaders[i], data, f, previewFontSize, 50, 0.6, 2), 0);
    if (previewRequiredWidth > previewInnerWidth) {
      this.onToggleShowToastEmitter.emit({
        severity: 'warn',
        title: 'PDF Export Warning',
        message: 'Some columns exceed the page width and may be truncated in the PDF. For complete data, use Excel export.',
        duration: 6000
      });
    }
    this.exportCurrentDataToPDF({
      title: `${(BREADCRUMB_NAMES[currentPath] || 'Export').replace(' ', '_')} - ${reportType}`,
      watermarkText: 'Dentalcorp',
      colorRules: [],
      forceSingleTable: true,
      pageSize: 'A3',
      pageOrientation: 'landscape',
      baseFontSize: this.visibleColumns !== null && this.visibleColumns.size > 10 ? 8 : 10,
      minFontSize: this.visibleColumns !== null && this.visibleColumns.size > 10 ? 6 : 8,
      targetPadding: 2,
      lineHeight: 0.9,
      fieldsToTrim: new Set(['parentKey', 'oldValue', 'newValue']),
      data: data,
      exportFileName: fileName
    });
  }

  private getExportFilters(): { [key: string]: any } {
    this.initializeDefaultFilterSelections();
    return JSON.parse(JSON.stringify(this.filters || {}));
  }

  private getExportColumns(): ColumnConfig[] {
    return this.gridConfig.columns
      .filter(col => this.visibleColumns.has(col.jsonKey))
      .filter(col => !(this.uniqueTableName === 'PMSAgentReleaseMapping' && col.jsonKey === 'downloadLink'))
      .map(column => ({ ...column }));
  }

  private extractRowsFromResponse(response: any): any[] {
    if (this.uniqueTableName === 'AuditReport') {
      return response?.data?.historyData || [];
    }

    return Array.isArray(response?.data) ? response.data : [];
  }

  // Export current data to PDF
  exportCurrentDataToPDF(options?: ExportPdfOptions): void {
    const data: any[] = options.data;
    const colConfigs: ColumnConfig[] = this.getExportColumns();

    // Build pdf columns from ColumnConfig
    const pdfColumns = colConfigs.map(c => ({
      header: c.label,
      field: c.jsonKey
    }));

    const title = options?.title ?? 'Export — Data';
    const watermarkText = options?.watermarkText ?? 'Dentalcorp';
    const pageSize = options?.pageSize ?? 'A4';
    const orientation = options?.pageOrientation ?? 'landscape';
    const baseFontSize = options?.baseFontSize ?? 10;
    const minFontSize = options?.minFontSize ?? 8;
    const targetPadding = options?.targetPadding ?? 0; // << minimal gaps
    const lineHeight = options?.lineHeight ?? 0.9;
    const forceSingleTable = options?.forceSingleTable ?? true;

    // Prepare headers/fields arrays
    const headers = pdfColumns.map(c => c.header.toUpperCase());
    const fields = pdfColumns.map(c => c.field);

    // Fit computation to keep all columns together
    const margins: [number, number, number, number] = [40, 60, 40, 40];
    const { fontSize: fittedFontSize, pageSize: fittedPageSize } =
      forceSingleTable
        ? computeFitSettings(headers, fields, data, pageSize, orientation, margins, baseFontSize, minFontSize, targetPadding)
        : { fontSize: baseFontSize, pageSize };

    // Detect if columns still exceed page width after fitting (truncation will occur)
    const fittedInnerWidth = innerWidth(fittedPageSize as any, orientation, margins[0], margins[2]);
    const totalRequiredWidth = fields.reduce((sum, f, i) =>
      sum + estimateColumnWidth(headers[i], data, f, fittedFontSize, 50, 0.6, targetPadding), 0);
    const willTruncate = totalRequiredWidth > fittedInnerWidth;

    // Coloring rules map
    const rules = options?.colorRules ?? [];
    const rulesMatch = (row: any, field: string) => {
      for (const r of rules) {
        const colOk = !r.columns || r.columns.includes(field);
        const rowOk = !r.rowWhen || r.rowWhen(row);
        const cellOk = !r.cellWhen || r.cellWhen(row, field);
        if (colOk && (rowOk || cellOk)) return r;
      }
      return undefined;
    };
    // Header row with minimal padding
    const headerRow = headers.map(text => ({
      text,
      bold: true,
      color: '#0D47A1',
      fillColor: '#E3F2FD',
      margin: [0, targetPadding, 0, targetPadding]
    }));

    // Build body rows: formatting -> sanitize -> truncate -> apply coloring rule -> minimal padding
    const body = [
      headerRow,
      ...data.map(row =>
        fields.map(field => {
          const cfg = colConfigs.find(c => c.jsonKey === field)!;

          // Your existing type-aware formatter
          let txt = formatCellByType(cfg, row[field], 'en-IN');
          if (!txt) txt = '';

          // Keep your current sanitization rules (newline strip + truncate 40)
          txt = txt.replace(/<br>/gi, ' ').replace(/\r?\n|\r/g, ' ').replace(/\s+/g, ' ').trim();
          if (options.fieldsToTrim?.has(field))
            if (txt.length > 40) txt = txt.slice(0, 40);
          // For ellipsis-width columns (long text), add a space after commas so pdfmake can wrap
          if (cfg?.width === 'ellipsis') txt = txt.replace(/,(?!\s)/g, ', ');

          const cell: any = {
            text: txt,
            alignment: cfg.dataType === 'number' ? 'right' : 'left',
            margin: [0, targetPadding, 0, targetPadding],
            lineHeight,
          };
          return cell;
        })
      )
    ];

    // All columns together: single widths array
    // Use '*' only for columns explicitly marked width:'ellipsis' (long wrapping text)
    // Everything else stays 'auto' so fixed-size columns don't get squeezed
    const widths = fields.map(f => {
      const cfg = colConfigs.find(c => c.jsonKey === f);
      return cfg?.width === 'ellipsis' ? '*' : 'auto';
    });

    // Doc definition
    const now = new Date();
    const generatedAt = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;

    const docDefinition: any = {
      info: { title, subject: `${title} (${generatedAt})` },
      pageSize: fittedPageSize,            // may be upgraded to A3
      pageOrientation: orientation,        // usually landscape
      pageMargins: margins,
      defaultStyle: { fontSize: fittedFontSize, lineHeight },   // applied globally

      watermark: { text: watermarkText, color: '#777', opacity: 0.12, angle: 35, bold: true },

      header: (currentPage: number, pageCount: number) => ({
        columns: [
          { text: title, style: 'headerLeft', color: '#1976D2' },
          { text: `Page ${currentPage} of ${pageCount}`, alignment: 'right', style: 'headerRight', color: '#555' }
        ],
        margin: [40, 20, 40, 0]
      }),

      footer: () => ({
        columns: [
          { text: `Generated: ${generatedAt}`, alignment: 'left', color: '#444' },
          { text: new Date().toLocaleTimeString(), alignment: 'right', color: '#444' }
        ],
        margin: [40, 10, 40, 0],
        fontSize: 9
      }),

      content: [
        { text: title, style: 'title', color: '#0D47A1', margin: [0, 0, 0, 8] },
        ...(willTruncate ? [{
          text: '⚠ Note: Some columns exceed the page width and their content may be truncated in this PDF. For full data, please use the Excel export.',
          color: '#B71C1C',
          fontSize: 8,
          margin: [0, 0, 0, 6]
        }] : []),
        {
          table: { headerRows: 1, widths, body },
          // Tight layout: zero paddings and thin borders
          layout: {
            paddingLeft: () => targetPadding,
            paddingRight: () => targetPadding,
            paddingTop: () => targetPadding,
            paddingBottom: () => targetPadding,
            hLineWidth: (i: number, node: any) => (i === 0 || i === node.table.body.length) ? 0.8 : 0.4,
            vLineWidth: () => 0.4,
            hLineColor: (i: number, node: any) => (i === 0 || i === node.table.body.length) ? '#B0BEC5' : '#CFD8DC',
            vLineColor: () => '#CFD8DC'
          }
        }
      ],

      styles: {
        title: { fontSize: Math.max(12, fittedFontSize + 2), bold: true },
        headerLeft: { fontSize: 9 },
        headerRight: { fontSize: 9 }
      }
    };

    pdfMake.createPdf(docDefinition).download(`${options.exportFileName}`);
  }

  toggleViewOptionsDropdown($event: MouseEvent): void {
    $event.stopPropagation();
    this.isViewOptionsOpen = !this.isViewOptionsOpen;
  }

  selectViewOption(option: string): void {
    this.selectedViewOption = option;
    delete this.filters['isActive'];
    delete this.filters['isInstalled'];
    delete this.filters['isLive'];

    switch (option) {
      case 'active':
        this.filters['isActive'] = true;
        break;
      case 'disabled':
        this.filters['isActive'] = false;
        break;
      case 'pending-installation':
        this.filters['isInstalled'] = false;
        break;
      case 'installed-practices':
        this.filters['isInstalled'] = true;
        break;
      case 'go-live':
        this.filters['isLive'] = true;
        break;
      // 'all': no filter
    }
    this.pageNumber = 1;
    this.fetchData();
  }

  getViewOptionLabel(): string {
    switch (this.selectedViewOption) {
      case 'active': return 'Active Practices';
      case 'disabled': return 'Disabled Practices';
      case 'pending-installation': return 'Pending Installation';
      case 'installed-practices': return 'Installed Practices';
      case 'go-live': return 'Live Practices';
      default: return 'All Practices';
    }
  }

  toggleShowAllPractices() {
    const currentFilter = this.filters['isActive'];
    if (currentFilter === false) {
      this.filters['isActive'] = true;
    } else {
      this.filters['isActive'] = false;
    }
    this.fetchData();
  }

  checkIfFromDisabledPractice(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const filter = urlParams.get('filter');

    this.isFromDisabledPractice = filter === 'disabled';

    if (filter === 'all' || filter === null) {
      delete this.filters['isActive'];
      this.selectedViewOption = 'all';
      return;
    }

    if (this.isFromDisabledPractice) {
      this.filters['isActive'] = false;
      this.selectedViewOption = 'disabled';
    }
  }

  checkIfFromPendingInstallation(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const filter = urlParams.get('filter');

    this.isFromPendingInstallation = filter === 'pending-installation';

    if (this.isFromPendingInstallation) {
      this.filters['isInstalled'] = false;
      this.selectedViewOption = 'pending-installation';
    }
  }

  checkIfFromInstalledPractice(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const filter = urlParams.get('filter');

    this.isFromInstalledPractice = filter === 'installed-practices';

    if (this.isFromInstalledPractice) {
      this.filters['isInstalled'] = true;
      this.selectedViewOption = 'installed-practices';
    }
  }

  checkIfFromGoLivePractice(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const filter = urlParams.get('filter');

    this.isFromGoLivePractice = filter === 'go-live';

    if (this.isFromGoLivePractice) {
      this.filters['isLive'] = true;
      this.selectedViewOption = 'go-live';
    }
  }

  toggleShowPendingInstallation() {
    const currentFilter = this.filters['isInstalled'];
    if (currentFilter === false) {
      delete this.filters['isInstalled'];
    } else {
      this.filters['isInstalled'] = false;
    }
    this.fetchData();
  }

  toggleShowGoLivePractices() {
    const currentFilter = this.filters['isLive'];
    if (currentFilter === true) {
      delete this.filters['isLive'];
    } else {
      this.filters['isLive'] = true;
    }
    this.fetchData();
  }

  isPracticePage(): boolean {
    return this._router.url.includes('/practices');
  }

  isDashboardPage(): boolean {
    return this._router.url.includes('/dashboard');
  }

  isDashboardSummaryGrid(): boolean {
    return this._router.url === '/dashboard' && this.uniqueTableName === 'PracticeMaster';
  }

  shouldHighlightPracticeRow(element: any): boolean {
    if (!this.isPracticePage()) return false;

    const isActive = element.isActive === true;
    const isInstalled = element.isInstalled === true;
    const freeDiskSpacePercent = element.freeDiskSpacePercentage;
    const ramGB = element.totalAvailableMemoryGBytes;

    return isActive && isInstalled &&
      ((freeDiskSpacePercent !== null && freeDiskSpacePercent !== undefined && freeDiskSpacePercent < 10) ||
        (ramGB !== null && ramGB !== undefined && ramGB < 16));
  }

  getPracticeRowHighlightClass(element: any): string {
    return this.shouldHighlightPracticeRow(element) ? 'practice-row-highlight' : '';
  }

  isUserManagementPage(): boolean {
    return this._router.url.includes('/user-role-management');
  }

  toggleShowInactiveUsers() {
    const currentFilter = this.filters['isActive'];
    if (currentFilter === false) {
      this.filters['isActive'] = true;
    } else {
      this.filters['isActive'] = false;
    }
    this.fetchData();
  }

  checkIfFromActiveUsers(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const filter = urlParams.get('filter');

    this.isFromActiveUsers = filter === 'active';

    if (this.isFromActiveUsers) {
      this.filters['isActive'] = true;
    }
  }

  checkIfFromActivePMS(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const filter = urlParams.get('filter');

    if (filter === 'disabled') {
      this.selectedConfigViewOption = 'disabled';
      this.filters['isActive'] = false;
    } else if (filter === 'active') {
      this.selectedConfigViewOption = 'active';
      this.filters['isActive'] = true;
    } else {
      this.selectedConfigViewOption = 'all';
      delete this.filters['isActive'];
    }

    this.isFromActivePMS = filter === 'active';
  }

  isConfigurationPage(): boolean {
    return this._router.url === '/dashboard/configuration/pms' ||
      this._router.url === '/dashboard/configuration/pms-objects' ||
      this._router.url === '/dashboard/configuration/phi-records' ||
      this._router.url === '/dashboard/configuration/load-schedules' ||
      this._router.url === '/dashboard/configuration/practice-load-configurations' ||
      this._router.url === '/dashboard/configuration/pms-schema-integrity' ||
      this._router.url === '/dashboard/configuration/dentrix-adjustment-types';
  }

  selectedConfigViewOption: string = 'all';

  getPageName(): string {
    switch (this._router.url) {
      case '/dashboard/configuration/pms':
        return 'PMS';
      case '/dashboard/configuration/pms-objects':
        return 'PMS Objects';
      case '/dashboard/configuration/phi-records':
        return 'PHI Records';
      case '/dashboard/configuration/load-schedules':
        return 'Load Schedules';
      case '/dashboard/configuration/practice-load-configurations':
        return 'Load Configurations';
      case '/dashboard/configuration/pms-schema-integrity':
        return 'PMS Schema Integrity';
      case '/dashboard/configuration/dentrix-adjustment-types':
        return 'Dentrix Adjustment Types';
      default:
        return '';
    }
  }

  getConfigViewOptionLabel(): string {
    switch (this.selectedConfigViewOption) {
      case 'all': return 'All ' + this.getPageName();
      case 'disabled': return 'Disabled ' + this.getPageName();
      default: return 'Active ' + this.getPageName();
    }
  }

  selectConfigViewOption(option: string): void {
    this.selectedConfigViewOption = option;
    switch (option) {
      case 'all':
        delete this.filters['isActive'];
        break;
      case 'disabled':
        this.filters['isActive'] = false;
        break;
      default: // 'active'
        this.filters['isActive'] = true;
        break;
    }
    this.pageNumber = 1;
    this.fetchData();
  }

  toggleShowInactivePMS() {
    this.selectConfigViewOption(
      this.filters['isActive'] === false ? 'active' : 'disabled'
    );
  }

  calculateLowResourceCount(): void {
    this.lowResourceCount = this.dataSource.data.filter((element: any) =>
      this.shouldHighlightPracticeRow(element)
    ).length;
  }

  //******************************// 
  // Column Prefrences //
  //******************************// 
  initializeVisibleColumns() {
    const hiddenColumns = this.columnPreference.HiddenColumns;
    this.gridConfig.columns.forEach(col => {
      if (!hiddenColumns.has(col.jsonKey)) {
        this.visibleColumns.add(col.jsonKey);
      }
    });
  }

  getPreferenceKeyName(): ColumnPreference {
    switch (this.uniqueTableName) {
      case 'PMSMaster':
        return {
          PreferenceKeyName: 'pms_master',
          HiddenColumns: new Set([])
        };
      case 'PracticeMaster':
        return {
          PreferenceKeyName: 'practice_master',
          HiddenColumns: new Set([])
        };
      case 'PMSObject':
        return {
          PreferenceKeyName: 'pms_object',
          HiddenColumns: new Set([])
        };
      case 'PHIMaster':
        return {
          PreferenceKeyName: 'phi_master',
          HiddenColumns: new Set([])
        };
      case 'LoadSchedule':
        return {
          PreferenceKeyName: 'load_schedule',
          HiddenColumns: new Set([])
        };
      case 'PracticeLoadConfiguration':
        return {
          PreferenceKeyName: 'practice_load_configuration',
          HiddenColumns: new Set([])
        };
      case 'PMSSchemaIntegrity':
        return {
          PreferenceKeyName: 'pms_schema_integrity',
          HiddenColumns: new Set([])
        };
      case 'DentrixAdjustmentType':
        return {
          PreferenceKeyName: 'dentrix_adjustment_type',
          HiddenColumns: new Set([])
        };

      case 'PracticeInstallationLog':
        return {
          PreferenceKeyName: 'practice_installation_log_report',
          HiddenColumns: new Set([])
        };
      case 'PMSAgentHealthStatusReport':
        return {
          PreferenceKeyName: 'pms_agent_health_status_report',
          HiddenColumns: new Set([])
        };
      case 'PracticeErrorLog':
        return {
          PreferenceKeyName: 'practice_error_log_report',
          HiddenColumns: new Set([])
        };
      case 'PracticeLoadDetail':
        return {
          PreferenceKeyName: 'practice_load_detail_report',
          HiddenColumns: new Set([])
        };
      case 'UserLogs':
        return {
          PreferenceKeyName: 'user_logs_report',
          HiddenColumns: new Set([])
        };
      case 'AuditReport':
        return {
          PreferenceKeyName: 'audit_report',
          HiddenColumns: new Set([])
        };
      case 'LastSevenDaysLoadReport':
        return {
          PreferenceKeyName: 'last_seven_days_load_report',
          HiddenColumns: new Set([])
        };

      case 'PMSAgentRelease':
        return {
          PreferenceKeyName: 'pms_agent_releasec',
          HiddenColumns: new Set([])
        };
      case 'PMSAgentReleaseMapping':
        return {
          PreferenceKeyName: 'pms_agent_release_mapping',
          HiddenColumns: new Set([])
        };

      case 'UserRoleManagement':
        return {
          PreferenceKeyName: 'user_role_management',
          HiddenColumns: new Set([])
        };

      default:
        return {
          PreferenceKeyName: '',
          HiddenColumns: new Set([])
        };
    }
  }

  toggleColumnSelector(event: MouseEvent) {
    event.stopPropagation();
    this.isColumnSelectorOpen = !this.isColumnSelectorOpen;
  }

  toggleColumnVisibility(jsonKey: string) {
    if (this.visibleColumns.has(jsonKey)) {
      this.visibleColumns.delete(jsonKey);
    } else {
      this.visibleColumns.add(jsonKey);
    }
  }

  isColumnVisible(jsonKey: string): boolean {
    return this.visibleColumns.has(jsonKey);
  }

  onSavePreferences(event: MouseEvent) {
    event.stopPropagation();
    this.saveColumnPreferences();
  }

  async loadColumnPreferences(preferenceKeyName: string): Promise<void> {
    if (!this.userId) {
      this.userId = localStorage.getItem('externalUserId');
      if (!this.userId) {
        try {
          const userClaims = await this.oktaAuth.getUser();
          this.userId = userClaims?.sub || null;
          if (this.userId) {
            localStorage.setItem('externalUserId', this.userId);
          }
        } catch (error) {
          console.warn('Could not get user claims:', error);
          this.userId = 'anonymous';
        }
      }
    }

    // Skip loading preferences if no valid user ID
    if (!this.userId || this.userId === 'null') {
      const cols = this.gridConfig.editable || this.gridConfig.deletable
        ? ['action', ...this.gridConfig.columns.map((c) => c.jsonKey)]
        : this.gridConfig.columns.map((c) => c.jsonKey);
      this.visibleColumns = new Set(cols);
      this.changeDetectorRef.detectChanges();
      return;
    }

    const subscription = (await this.columnPrefService.getColumnPreferences(preferenceKeyName))
      .pipe(takeUntil(this.destroy$))
      .subscribe(prefs => {
        if (prefs !== null) {
          const preferences = JSON.parse(prefs.preferenceValue);
          this.visibleColumns = new Set(preferences.columns);
        } else {
          const cols = this.gridConfig.editable || this.gridConfig.deletable
            ? ['action', ...this.gridConfig.columns.map((c) => c.jsonKey)]
            : this.gridConfig.columns.map((c) => c.jsonKey);
          this.visibleColumns = new Set(cols);
        }
        this.changeDetectorRef.detectChanges();
      });

    this.subscriptions.push(subscription);
  }

  saveColumnPreferences(): void {
    this.isSavingPreferences = true;

    if (!this.userId) {
      this.userId = localStorage.getItem('externalUserId');
    }

    // Skip saving if no valid user ID
    if (!this.userId || this.userId === 'null' || this.userId === 'anonymous') {
      const toast: Toast = {
        severity: 'warn',
        title: 'Warning',
        message: 'Cannot save preferences: User not authenticated',
        duration: 3000
      };
      this.onToggleShowToastEmitter.emit(toast);
      this.isColumnSelectorOpen = false;
      this.isSavingPreferences = false;
      this.changeDetectorRef.markForCheck();
      return;
    }

    const body = {
      externalUserId: this.userId,
      preferenceKey: this.columnPreference.PreferenceKeyName,
      preferenceValue: JSON.stringify({ columns: Array.from(this.visibleColumns) })
    };

    const subscription = this.columnPrefService
      .saveColumnPreferences(this.columnPreference.PreferenceKeyName, body)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isSavingPreferences = false;
          this.changeDetectorRef.markForCheck();
        })
      )
      .subscribe({
        next: (ok) => {
          const toast: Toast = {
            severity: 'success',
            title: 'Success',
            message: 'Column preferences saved successfully',
            duration: 3000
          };
          this.onToggleShowToastEmitter.emit(toast);
          this.isColumnSelectorOpen = false;
          this.isSavingPreferences = false;
          this.changeDetectorRef.markForCheck();
        },
        error: (err) => {
          const toast: Toast = {
            severity: 'error',
            title: 'Error',
            message: 'Failed to save column preferences',
            duration: 3000
          };
          this.onToggleShowToastEmitter.emit(toast);
          this.isColumnSelectorOpen = false;
          this.isSavingPreferences = false;
          this.changeDetectorRef.markForCheck();
        }
      });

    this.subscriptions.push(subscription);
  }

  // Practice Actions Methods
  togglePracticeMenu(practiceId: string, event: MouseEvent): void {
    event.stopPropagation();
    // Close all other menus
    Object.keys(this.practiceMenuOpen).forEach(key => {
      if (key !== practiceId) {
        this.practiceMenuOpen[key] = false;
      }
    });
    this.practiceMenuOpen[practiceId] = !this.practiceMenuOpen[practiceId];
  }

  startOnDemandLoad(practice: any): void {
    this.practiceMenuOpen[practice.id] = false;

    const payload = {
      PracticeId: practice.id,
      PracticeCode: practice.practiceCode,
      ActionByUser: 'The on-Demand load started from the Admin UI by ' + localStorage.getItem('userEmail'),
      Action: 'START_ON_DEMAND_LOAD',
      ActionParameters: ''
    };

    // Show immediate toast to confirm method is called
    this.onToggleShowToastEmitter.emit({
      severity: 'info',
      title: 'Info',
      message: 'Sending on-demand load request...',
    });

    const subscription = this.gridDataService.createData(API_ENDPOINTS.PUBLISH_TOPIC, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.onToggleShowToastEmitter.emit({
            severity: 'success',
            title: 'Success',
            message: 'The on-demand load message is sent to the practice.',
          });
          this.fetchData();
        },
        error: (err: any) => {
          this.onToggleShowToastEmitter.emit({
            severity: 'error',
            title: 'Error',
            message: err.error?.errorMessage || 'Failed to start on-demand load',
          });
        },
      });

    this.subscriptions.push(subscription);
  }

  restartAgentService(practice: any): void {
    this.practiceMenuOpen[practice.id] = false;

    const payload = {
      PracticeId: practice.id,
      PracticeCode: practice.practiceCode,
      ActionByUser: 'The Agent Services restarted from the Admin UI by ' + localStorage.getItem('userEmail'),
      Action: 'RESTART_AGENT_SERVICES',
      ActionParameters: ''
    };

    // Show immediate toast
    this.onToggleShowToastEmitter.emit({
      severity: 'info',
      title: 'Info',
      message: 'Sending restart agent request...',
    });

    const subscription = this.gridDataService.createData(API_ENDPOINTS.PUBLISH_TOPIC, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.onToggleShowToastEmitter.emit({
            severity: 'success',
            title: 'Success',
            message: 'Agent restart services message sent to the practice.',
          });
          this.fetchData();
        },
        error: (err: any) => {
          this.onToggleShowToastEmitter.emit({
            severity: 'error',
            title: 'Error',
            message: err.error?.errorMessage || 'Failed to restart agent service',
          });
        },
      });

    this.subscriptions.push(subscription);
  }

  confirmResetInstallation(practice: any): void {
    this.practiceMenuOpen[practice.id] = false;
    this.selectedPractice = practice;
    this.showResetConfirmation = true;
  }

  resetInstallation(): void {
    if (!this.selectedPractice) return;

    const resetURL = API_ENDPOINTS.PRACTICE_RESET_INSTALLATION.replace(':id', this.selectedPractice.id);

    const subscription = this.gridDataService.createData(resetURL, null)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.onToggleShowToastEmitter.emit({
            severity: 'success',
            title: 'Success',
            message: 'Successfully reset the installation for the practice',
          });
          this.fetchData();
        },
        error: (err: any) => {
          this.onToggleShowToastEmitter.emit({
            severity: 'error',
            title: 'Error',
            message: err.error?.errorMessage || 'Failed to reset installation',
          });
        },
      });

    this.subscriptions.push(subscription);

    this.showResetConfirmation = false;
    this.selectedPractice = null;
  }

  cancelResetInstallation(): void {
    this.showResetConfirmation = false;
    this.selectedPractice = null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.activeDataFetchSubscription?.unsubscribe();

    // Clean up any remaining subscriptions
    this.subscriptions.forEach(sub => {
      if (sub && !sub.closed) {
        sub.unsubscribe();
      }
    });
    this.subscriptions = [];
  }

  isExtraActionVisible(action: { label: string; icon?: string; action: () => void; cssClass?: string }): boolean {
    const normalizedLabel = (action?.label ?? '').toLowerCase();
    if (!normalizedLabel.includes('smart schedule')) {
      return true;
    }

    const userRole = (localStorage.getItem('userRole') ?? '').trim().toUpperCase();
    return userRole === 'ADMIN' || userRole === 'SUPER ADMIN';
  }
}
